import type { PostsLoaderTypeKey, PostsLoaderTypes } from '@/Components/posts/loaders';
import { getIsStatus, getStatusTypeObject } from '@/Components/posts/status';
import { PostStatusEnum } from '@/graphql/types';
import { Box, Checkbox, Skeleton, Tooltip } from '@mui/material';
import { useMemo } from 'react';
import { toast } from 'react-toastify';
import { useMultiViewPickerStore } from '../../MultiViewPickerStore';
import MoreDialog, { type MoreDialogItem } from './MoreDialog/MoreDialog';

import './GridCard.less';

type GridDetailsType =
  | {
      label?: string | number | null;
      icon?: React.JSX.Element | null;
      tooltip?: string | null;
    }
  | null
  | false
  | undefined;

type SelectType = {
  selectedIds: string[];
  initIds: string[];
  toType: 'znglist' | 'category' | 'catalog';
  limit: number;
  setStore: (
    value: Partial<{
      toId: string;
      initIds: string[];
      selectedIds: string[];
      limit: number;
    }>
  ) => void;
};

export type GridCardProps = {
  id: string;
  header: string;
  headerLink?: string;
  subheader?: string;
  imageUrl?: string;
  menuItems?: MoreDialogItem[];
  useHoverButtons?: boolean;
  gridDetails?: GridDetailsType[];
  select?: SelectType;
  isLast?: boolean;
  isFallback?: boolean;
  showCheckbox?: boolean;
};

// Component shown when hovering over image
const HoverButtons = ({ overlayButtons }: { overlayButtons?: MoreDialogItem[] }) => {
  return (
    <div className="card-hover-btns">
      {overlayButtons
        ?.filter((i) => (i?.location === undefined || i?.location === 'hover-menu') && !!i)
        ?.map((item, idx) => (
          <Tooltip
            key={`hover_button_${idx.toString()}`}
            slotProps={{ tooltip: { className: 'bordered' } }}
            title={item?.label}
          >
            <button type="button" className="card-hover-btn" onClick={item?.action}>
              {item?.icon}
            </button>
          </Tooltip>
        ))}
    </div>
  );
};

type GridCheckboxProps<T extends PostsLoaderTypeKey> = PostsLoaderTypes[T]['paginationNodeType'];

const GridCheckbox = <T extends PostsLoaderTypeKey>({ id }: GridCheckboxProps<T>) => {
  const updateNode = useMultiViewPickerStore((state) => state.updateNode);
  const node = useMultiViewPickerStore((state) => state.nodes.get(id));

  if (!node) return null;

  return (
    <Checkbox
      className="grid-card-ckbx"
      checked={node?.checked}
      onChange={() => {
        updateNode(id, { ...node, checked: !node?.checked });
      }}
    />
  );
};

type GridCardPropsType<T extends PostsLoaderTypeKey> = {
  node?: PostsLoaderTypes[T]['paginationNodeType'];
} & GridCardProps;

export const GridCard = <T extends PostsLoaderTypeKey>(props: GridCardPropsType<T>) => {
  // If there's no link prop, don't use anchor tag
  const headerComponent = useMemo(() => {
    if (!!props?.headerLink && !props?.select) {
      return (
        <a href={props.headerLink}>
          <h2>{props.header}</h2>
        </a>
      );
    }
    if (!props.header && props?.isFallback) return <Skeleton variant="text" width="100%" />;
    return <h2 className="grid-card-title">{props.header}</h2>;
  }, [props.header, props?.headerLink, props?.select, props?.isFallback]);

  // Handle click when in the select view
  const onClickHandler = () => {
    if (!props?.select) return;
    if (props.select?.selectedIds.includes(props.id)) {
      props.select.setStore({
        selectedIds: props.select.selectedIds.filter((id) => !(id === props.id)),
      });
      return;
    }

    if (props.select.selectedIds.length === props.select.limit) {
      toast.warning(
        `You can only add ${props.select.limit} items to a ${
          props.select.toType.charAt(0).toUpperCase() + props.select.toType.slice(1)
        }`
      );
      return;
    }

    props.select.setStore({ selectedIds: [...props.select.selectedIds, props.id] });
  };

  const metadataTags = useMemo(
    () =>
      (props?.gridDetails ?? [])
        .filter((item) => !!item)
        .map((item, idx) => (
          <Tooltip
            key={`grid_detail_${idx.toString()}`}
            slotProps={{ tooltip: { className: 'bordered' } }}
            title={item.tooltip ?? item.label}
            placement="top"
          >
            <div className="grid-card-tag-container">
              <>
                {item?.icon}
                {!item?.label && props?.isFallback ? (
                  <Skeleton variant="text" width="100%" sx={{ ml: 0.5 }} />
                ) : (
                  <p>{item?.label}</p>
                )}
              </>
            </div>
          </Tooltip>
        )),
    [props?.gridDetails, props?.isFallback]
  );

  const statusTypeObject = useMemo(() => {
    if (!!props?.node && 'statusSettings' in props.node) {
      return getStatusTypeObject(props?.node?.statusSettings);
    }
    return null;
  }, [props?.node]);

  // Show hover buttons if there are menu items and useHoverButtons is true and for catalog view
  const showHoverButtons = useMemo(() => {
    return !!props?.menuItems && props?.useHoverButtons && (!props?.select || props?.select?.toType === 'catalog');
  }, [props?.menuItems, props?.useHoverButtons, props?.select]);

  const selectedClass = useMemo(() => {
    if (props?.select?.selectedIds.includes(props.id)) return 'selected';
    if (!!props?.select) return 'unselected';
    return '';
  }, [props?.select]);

  return (
    <>
      <Box
        className={`grid-card-container ${selectedClass}`}
        onClick={onClickHandler}
        style={{
          ...(props?.select !== null ? { borderRadius: 'unset' } : {}),
        }}
        sx={{
          ...(getIsStatus(statusTypeObject?.value, [PostStatusEnum.Pending]) && {
            background: 'rgba(255, 133, 0, 0.1)',
          }),
        }}
      >
        {/* Image Section */}
        {props?.imageUrl ? (
          <div className="grid-card-image">
            {!!props.select && (
              <Checkbox
                className="zn-chbx-2 grid-card-select-view-ckbx"
                checked={props.select?.selectedIds.includes(props.id)}
              />
            )}
            {props?.showCheckbox && props?.node?.id && <GridCheckbox {...props?.node} />}

            {props?.isFallback ? (
              <Skeleton variant="rectangular" style={{ width: '100%', height: '100%' }} />
            ) : (
              <img src={props.imageUrl} />
            )}

            {showHoverButtons && !props.isFallback && (
              <div className="grid-card-overlay">
                <HoverButtons overlayButtons={props?.menuItems} />
              </div>
            )}
          </div>
        ) : null}
        {!props?.imageUrl && props?.isFallback && (
          <div className="grid-card-image">
            <Skeleton variant="rectangular" style={{ width: '100%', height: '100%' }} />
          </div>
        )}

        {/* Details section */}
        <div className="grid-text-details">
          {/* Heading */}
          <div className="grid-card-heading">
            <Tooltip title={props.header} slotProps={{ tooltip: { className: 'bordered' } }} placement="top-start">
              {headerComponent}
            </Tooltip>
            {!!props?.menuItems && !props?.select && <MoreDialog items={props.menuItems} />}
          </div>

          {/* Subheading */}
          {props?.subheader && <h3 className="grid-card-description">{props.subheader}</h3>}

          {/* Metadata details */}
          {!!props?.gridDetails && <div className="grid-card-tags">{metadataTags}</div>}
        </div>
      </Box>
    </>
  );
};

export default GridCard;
